import Card from '@/components/Card';
import { FormField } from '@/components/Form/FormField';
import { FieldValues } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { StatusTag } from '@/components/Tag/StatusTag';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { FormFieldReportSection } from '@/components/Form/FormFieldReportSection';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { getFormattedDate, getFormattedTime } from '@/utils/date.utils';
import { Link } from 'react-router-dom';
import { getPreparedEntityRoleLabel } from '@/utils/user-entity-role';
import { twMerge } from 'tailwind-merge';
import { getTranslatedItem } from '@/utils/language.utils';

export const VehiculeCheckViewInfos = <TSchema extends FieldValues>({
  t,
  data,
  isReadOnly,
}: FormManagementProps<TSchema>) => {
  const isIndividual = data?.actionUserType === ActionUserType.INDIVIDUAL;
  const client = data?.client;

  const { fullEntityRoleLabel } = getPreparedEntityRoleLabel({
    t,
    roleType: client?.roleType,
    groups: client?.groups,
    companies: client?.companies,
    branches: client?.branches,
  });

  const isIndividualUser = data?.actionUserType === ActionUserType.INDIVIDUAL;

  const isToModerate =
    data?.humanModerationStatus === HumanModerationStatus.A_MODERER;

  const userFullname = `${client?.firstname} ${client?.lastname}`;

  const translatedVehiculeCheckType = getTranslatedItem(
    data?.vehiculeCheckType?.['translatedVehiculeCheckTypes']
  );

  return (
    <Card
      title={
        <TitleSection
          text={t('common:informations')}
          defaultIconClassName="ki-badge"
        />
      }
      bodyClassName="py-8 flex flex-col gap-5"
    >
      <>
        <FormFieldReportSection>
          <FormField
            text={`${t('common:createdAt')} :`}
            textClassName="font-semibold text-slate-500"
          >
            <div>
              <span className="text-sm tracking-wide">
                {getFormattedDate(data?.createdAt, true)}
              </span>
              &nbsp;
              <span className="text-sm">
                {t('common:at').toLowerCase()}
                &nbsp;
                {getFormattedTime(data?.createdAt)}
              </span>
            </div>
          </FormField>
          <FormField
            text={`${t('inspectionId')} :`}
            textClassName="font-semibold text-slate-500"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <p className="text-[13.7px] text-black">{data?.shortId}</p>
            </div>
          </FormField>
          {data?.referenceNumber && (
            <FormField
              text={`${t('common:referenceNumber_short_2')} :`}
              textClassName="font-semibold text-slate-500"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <p className="text-sm">{data?.referenceNumber}</p>
              </div>
            </FormField>
          )}
        </FormFieldReportSection>
        <FormFieldReportSection>
          <FormField
            text={`${t('common:performedBy')} :`}
            textWrapperClassName="mt-1"
            textClassName="font-semibold text-slate-500"
          >
            <StatusTag
              text={`${
                isIndividualUser
                  ? t('common:individual')
                  : t('common:professional')
              }`}
              className={'text-purple-600 border-purple-400 bg-purple-100/40'}
            />
          </FormField>
          <FormField
            text={`${t('common:by')} :`}
            containerClassName="mt-2.5"
            textWrapperClassName="mt-1"
            textClassName="font-semibold text-slate-500"
          >
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center">
                <div className="flex items-center gap-1.5">
                  <i className="-mt-0.75 ki-filled ki-user text-slate-500 text-[18px]" />
                  <div className="text-[13.7px] text-slate-600">
                    {userFullname}
                  </div>
                </div>
                {!isReadOnly && (
                  <Link
                    to={
                      isIndividual
                        ? `/vehicule-check/view/${data?.id}?tab=client`
                        : `/user/edit/${data?.actionUserId}`
                    }
                    className="ml-[8px] px-[5px] pt-1 pb-[5.5px] flex justify-center items-center bg-primary rounded-lg shadow-md hover:cursor-pointer hover:opacity-80"
                  >
                    <i
                      className={twMerge(
                        'ki-outline ki-cursor text-white text-[18px]',
                        isIndividual && 'ki-right-square'
                      )}
                    />
                  </Link>
                )}
              </div>
              {!isReadOnly && !isIndividual && fullEntityRoleLabel && (
                <>
                  <div className="text-slate-400/45 font-light">|</div>
                  <StatusTag
                    text={fullEntityRoleLabel}
                    className={
                      'text-slate-600 border-slate-400/50 bg-transparent py-[4.5px]'
                    }
                  />
                </>
              )}
            </div>
          </FormField>
        </FormFieldReportSection>
        <FormFieldReportSection>
          <FormField
            text={`${t('common:inspectionType')} :`}
            containerClassName="items-center"
            textClassName="font-semibold text-slate-500"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <p className="text-[13.7px] text-slate-700">
                {translatedVehiculeCheckType?.name ||
                  data?.vehiculeCheckType?.name}
              </p>
            </div>
          </FormField>
        </FormFieldReportSection>
        {!isReadOnly && (
          <FormFieldReportSection>
            <FormField
              text={`${t('common:moderationStatus')} :`}
              containerClassName="items-center"
              textClassName="font-semibold text-slate-500"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <StatusTag
                  text={`${
                    isToModerate
                      ? t('common:toModerate')
                      : t('common:moderated')
                  }`}
                  className={
                    isToModerate
                      ? 'bg-red-FF2929/10 text-red-FF2929'
                      : 'bg-green-EAFFF2 border-green-400 text-green-11BFA4 tracking-[0.005em]'
                  }
                />
              </div>
            </FormField>
          </FormFieldReportSection>
        )}
      </>
    </Card>
  );
};
